import { useCallback, useContext, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import UserContext from "../context/UserContext";
import CustomSpinner from "../components/CustomSpinner";
import userService from "../services/userService";
import { ROLES } from "../constants/business";
import { PIECEWORK_TYPES } from "../constants/business";

const emptyUser = { name: "", user: "", rol: "Almacén", password: "", pieceworkPrice: "", pieceworkPrices: { tinaco_1100: "", tinaco_3200: "", tinaco_5200: "" } };

export default function Users() {
  const { user, loading, setLoading } = useContext(UserContext);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(emptyUser);
  const [editingId, setEditingId] = useState(null);
  const [passwordUser, setPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [message, setMessage] = useState("");

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      setUsers((await userService.getUsers()).data);
    } catch (error) {
      setMessage(error.response?.data?.message || "No se pudieron cargar los usuarios.");
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  useEffect(() => {
    if (user?.rol === "Administrador") loadUsers();
  }, [user, loadUsers]);

  const saveUser = async (event) => {
    event.preventDefault();
    if (!form.name.trim() || !form.user.trim() || (!editingId && form.password.length < 8)) return;
    try {
      setLoading(true);
      if (editingId) await userService.updateUser(editingId, form);
      else await userService.createUser(form);
      setForm(emptyUser);
      setEditingId(null);
      setMessage("Usuario guardado correctamente.");
      await loadUsers();
    } catch (error) {
      setMessage(error.response?.data?.message || "No se pudo guardar el usuario.");
      setLoading(false);
    }
  };

  const editUser = (item) => {
    setForm({ name: item.name, user: item.user, rol: item.rol, password: "", pieceworkPrice: item.pieceworkPrice || "", pieceworkPrices: { tinaco_1100: item.pieceworkPrices?.tinaco_1100 || "", tinaco_3200: item.pieceworkPrices?.tinaco_3200 || "", tinaco_5200: item.pieceworkPrices?.tinaco_5200 || "" } });
    setEditingId(item.id);
  };

  const deleteUser = async (item) => {
    if (!window.confirm(`¿Eliminar la cuenta de ${item.name}?`)) return;
    try {
      setLoading(true);
      await userService.deleteUser(item.id);
      setUsers((current) => current.filter((value) => value.id !== item.id));
      setMessage("Usuario eliminado correctamente.");
    } catch (error) {
      setMessage(error.response?.data?.message || "No se pudo eliminar el usuario.");
    } finally {
      setLoading(false);
    }
  };

  const savePassword = async (event) => {
    event.preventDefault();
    if (newPassword.length < 8) return;
    try {
      setLoading(true);
      await userService.changePassword(passwordUser.id, newPassword);
      setPasswordUser(null);
      setNewPassword("");
      setMessage("Contraseña actualizada correctamente.");
    } catch (error) {
      setMessage(error.response?.data?.message || "No se pudo actualizar la contraseña.");
    } finally {
      setLoading(false);
    }
  };

  if (!user || user.rol !== "Administrador") return <Navigate to="/" replace />;

  const input = (label, value, onChange, props = {}) => (
    <label className="block text-sm font-medium">
      {label}
      <input className="form-control mt-2" value={value} onChange={onChange} {...props} />
    </label>
  );

  return (
    <div className="app-shell banner-1">
      {loading && <CustomSpinner />}
      <div className="app-panel max-w-6xl text-center">
        <div className="mb-6 text-left">
          <h1 className="text-2xl font-semibold">Usuarios</h1>
        </div>

        {message && <div className="mb-5 rounded-xl border border-sky-500/30 bg-sky-500/10 px-4 py-3 text-left text-sm text-sky-700 dark:text-sky-300">{message}</div>}

        <form onSubmit={saveUser} className="mb-6 grid gap-3 text-left md:grid-cols-2 xl:grid-cols-3">
          {input("Nombre", form.name, (e) => setForm({ ...form, name: e.target.value }), { required: true })}
          {input("Usuario", form.user, (e) => setForm({ ...form, user: e.target.value }), { disabled: Boolean(editingId), required: true })}
          <label className="block text-sm font-medium">Rol<select className="form-control mt-2" value={form.rol} onChange={(e) => setForm({ ...form, rol: e.target.value })}>{ROLES.map((role) => <option key={role}>{role}</option>)}</select></label>
          {form.rol === "Producción" && PIECEWORK_TYPES.map(({ value, label }) => input(`Precio ${label}`, form.pieceworkPrices[value], (e) => setForm({ ...form, pieceworkPrices: { ...form.pieceworkPrices, [value]: e.target.value } }), { key: value, type: "number", min: "0", step: "0.01", required: true }))}
          {!editingId && input("Contraseña", form.password, (e) => setForm({ ...form, password: e.target.value }), { type: "password", minLength: 8, required: true })}
          <div className="flex items-end gap-2"><button className="btn btn-success" type="submit">{editingId ? "Actualizar usuario" : "Crear usuario"}</button>{editingId && <button className="btn border-slate-300 dark:border-slate-700" type="button" onClick={() => { setEditingId(null); setForm(emptyUser); }}>Cancelar</button>}</div>
        </form>

        <div className="overflow-x-auto text-left"><table className="w-full text-sm"><thead className="border-b border-black/10 dark:border-white/10"><tr><th className="p-3">Usuario</th><th className="p-3">Nombre</th><th className="p-3">Rol</th><th className="p-3">Acciones</th></tr></thead><tbody className="divide-y divide-black/10 dark:divide-white/10">{users.map((item) => <tr key={item.id}><td className="p-3">{item.user}</td><td className="p-3">{item.name}</td><td className="p-3">{item.rol}</td><td className="flex flex-wrap gap-2 p-3"><button className="btn btn-info px-2 py-1" onClick={() => editUser(item)}>Editar</button><button className="btn btn-warning px-2 py-1" onClick={() => setPasswordUser(item)}>Contraseña</button><button className="btn btn-danger px-2 py-1" onClick={() => deleteUser(item)}>Eliminar</button></td></tr>)}</tbody></table></div>
      </div>

      {passwordUser && <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4"><div className="w-full max-w-md rounded-2xl bg-slate-50 p-6 text-left shadow-2xl dark:bg-slate-900"><h2 className="text-lg font-semibold">Nueva contraseña</h2><form onSubmit={savePassword} className="mt-4">{input("Contraseña", newPassword, (e) => setNewPassword(e.target.value), { type: "password", minLength: 8, required: true })}<div className="mt-4 flex gap-2"><button className="btn btn-success" type="submit">Guardar</button><button className="btn border-slate-300 dark:border-slate-700" type="button" onClick={() => setPasswordUser(null)}>Cancelar</button></div></form></div></div>}
    </div>
  );
}